import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Twitter, Instagram, Youtube } from 'lucide-react';
import { companyInfo, navLinks } from '../data/mock';
import { publicAPI } from '../services/api';

const Footer = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await publicAPI.getServices();
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };
    fetchServices();
  }, []);

  return (
    <footer className="bg-[#005566] text-gray-300">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src={companyInfo.logo} 
                alt="Nanda Diagnostics" 
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/assets/nanda-logo.png';
                }}
              />
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              A diagnostic center accredited for its innovation and merits in the field of radiology and pathology.
            </p>
            <div className="flex gap-4">
              <a 
                href={companyInfo.socialMedia.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#007897] rounded-lg flex items-center justify-center hover:bg-[#008fa3] transition-colors"
              >
                <Facebook size={18} />
              </a>
              <a 
                href={companyInfo.socialMedia.twitter} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#007897] rounded-lg flex items-center justify-center hover:bg-[#008fa3] transition-colors"
              >
                <Twitter size={18} />
              </a>
              <a 
                href={companyInfo.socialMedia.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#007897] rounded-lg flex items-center justify-center hover:bg-[#008fa3] transition-colors"
              >
                <Instagram size={18} />
              </a>
              <a 
                href={companyInfo.socialMedia.youtube} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#007897] rounded-lg flex items-center justify-center hover:bg-[#008fa3] transition-colors"
              >
                <Youtube size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="hover:text-blue-300 transition-colors flex items-center gap-2"
                  >
                    <span className="w-1.5 h-1.5 bg-[#00a0af] rounded-full" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.id}>
                  <Link 
                    to={`/services/${service.slug}`}
                    className="hover:text-blue-300 transition-colors flex items-center gap-2"
                  >
                    <span className="w-1.5 h-1.5 bg-[#00a0af] rounded-full" />
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href={companyInfo.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 hover:text-blue-300 transition-colors"
                >
                  <MapPin size={20} className="text-[#00a0af] mt-0.5 flex-shrink-0" />
                  <span>{companyInfo.address}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`tel:${companyInfo.phones[0]}`}
                  className="flex items-center gap-3 hover:text-blue-300 transition-colors"
                >
                  <Phone size={20} className="text-[#00a0af] flex-shrink-0" />
                  <span>{companyInfo.phones[0]}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-3 hover:text-blue-300 transition-colors"
                >
                  <Mail size={20} className="text-[#00a0af] flex-shrink-0" />
                  <span>{companyInfo.email}</span>
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={20} className="text-[#00a0af] flex-shrink-0 mt-0.5" />
                <div>
                  <p>{companyInfo.timings.weekdays}</p>
                  <p>{companyInfo.timings.sunday}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#007897]">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Nanda Diagnostics. All Rights Reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy-policy" className="hover:text-blue-300 transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="hover:text-blue-300 transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
